import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * Who is signed in, for this edition.
 *
 * `/v1/me` answers from the session cookie and the Host — there is no tenant in the URL,
 * because the Host *is* the tenant. That is why the admin console has no `$tenantSlug` segment
 * even though the router guide shows one: the guide's example is a product where one user
 * switches between organizations, and this one is a per-edition deployment where switching
 * means going to another hostname.
 */
export const sessionSchema = z.object({
  user: z
    .object({
      id: z.string(),
      email: z.string(),
      name: z.string().nullable(),
    })
    .nullable(),
  personId: z.string().nullable(),
  permissions: z.array(z.string()),
  /**
   * The member of platform staff behind this session, when it is an impersonation — null for
   * every ordinary one. `.default(null)` so a console deployed ahead of the API still parses the
   * older response rather than calling it out of date.
   */
  impersonator: z
    .object({ id: z.string(), email: z.string(), name: z.string().nullable() })
    .nullable()
    .default(null),
  /**
   * Set when this login is a test account (see `features/auth/test-personas.ts`). Defaults to null
   * for the same deploy-skew reason as `impersonator`.
   */
  testPersona: z.object({ key: z.string(), label: z.string() }).nullable().default(null),
})

export type Session = z.infer<typeof sessionSchema>

export async function fetchSession(signal?: AbortSignal): Promise<Session> {
  return api.get('/v1/me', { schema: sessionSchema, ...(signal ? { signal } : {}) })
}

/**
 * Does the caller hold a permission?
 *
 * The same implication the server applies: `.manage` implies `.write` implies `.read`, one
 * way, and `*.*` satisfies everything. Duplicated deliberately — the console uses it to decide
 * which buttons exist, and a button the server would refuse is worse than no button.
 *
 * It is **not** an authorization check. The server's guard is the enforcement; this only keeps
 * the UI honest about what it is offering.
 */
const IMPLIED: Record<string, readonly string[]> = {
  manage: ['manage', 'view', 'write', 'read'],
  // The console's read-only tier. It implies the portal's `read`; `read` never implies it.
  view: ['view', 'read'],
  write: ['write', 'read'],
  read: ['read'],
}

export function can(session: Session | undefined, required: string): boolean {
  const granted = session?.permissions ?? []
  if (granted.includes('*.*')) return true
  if (granted.includes(required)) return true

  const [resource, action] = required.split('.')
  if (resource === undefined || action === undefined) return false

  return granted.some((grant) => {
    const [grantResource, grantAction] = grant.split('.')
    if (grantResource !== resource || grantAction === undefined) return false
    return (IMPLIED[grantAction] ?? []).includes(action)
  })
}
